import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, AlertCircle, ArrowLeft, Sparkles, MessageCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function DemoRoom() {
  const { classId } = useParams();
  const nav = useNavigate();
  const [cls, setCls] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/demo-classes/${classId}`)
      .then(({ data }) => setCls(data))
      .catch((e) => setError(e.response?.data?.detail || "Demo class not found"));
  }, [classId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="glass-strong rounded-2xl p-8 max-w-md text-center" data-testid="demo-error">
          <AlertCircle className="w-8 h-8 text-amber-400 mx-auto" />
          <div className="font-display text-xl font-semibold mt-3">Demo unavailable</div>
          <div className="text-sm text-[var(--text-dim)] mt-2">{error}</div>
          <button onClick={() => nav("/")} className="btn-primary mt-5">Go home</button>
        </div>
      </div>
    );
  }
  if (!cls) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--text-dim)]">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading demo class…
      </div>
    );
  }

  const url = cls.demo_video_url || "";
  const isYouTube = /youtube\.com|youtu\.be/i.test(url);
  const isVimeo = /vimeo\.com/i.test(url);
  const isLoom = /loom\.com/i.test(url);
  const isDirect = /\.(mp4|webm|m3u8)(\?|$)/i.test(url);
  const ytId = (() => {
    const m = url.match(/(?:embed\/|v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    return m ? m[1] : null;
  })();
  const embed = isYouTube && ytId ? `https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`
    : isVimeo ? url.replace("vimeo.com/", "player.vimeo.com/video/") + "?autoplay=1"
    : isLoom ? (url.includes("/embed/") ? url : url.replace("/share/", "/embed/"))
    : url;

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] flex flex-col" data-testid="demo-room">
      <div className="border-b border-white/5 px-4 py-3 flex items-center justify-between bg-[rgba(7,9,15,0.7)] backdrop-blur">
        <button onClick={() => nav("/")} className="btn-ghost text-xs"><ArrowLeft className="w-4 h-4" /> Home</button>
        <div className="min-w-0 text-center px-3">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300">Live Demo Session</div>
          <div className="text-sm font-semibold truncate">{cls.title}</div>
        </div>
        <a href="https://wa.me/919131341638" target="_blank" rel="noreferrer" className="btn-primary text-xs">
          <MessageCircle className="w-4 h-4" /> Enroll
        </a>
      </div>

      <div className="flex-1 grid lg:grid-cols-4 gap-0">
        <div className="lg:col-span-3 bg-black flex items-center justify-center">
          {isDirect ? (
            <video src={url} controls autoPlay className="w-full h-full max-h-[80vh] object-contain" data-testid="demo-video" />
          ) : (
            <div className="relative w-full" style={{ aspectRatio: "16/9", maxHeight: "85vh" }}>
              <iframe
                src={embed}
                title={cls.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="demo-iframe"
              />
            </div>
          )}
        </div>
        <aside className="bg-[var(--bg-elev-1)] border-l border-white/5 p-5 flex flex-col">
          <div className="text-xs uppercase tracking-wider text-fuchsia-300">Now playing</div>
          <h2 className="font-display text-xl font-semibold mt-1">{cls.title}</h2>
          {cls.description && <p className="text-sm text-[var(--text-dim)] mt-3">{cls.description}</p>}
          <div className="mt-5 glass rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-fuchsia-300">Liked the teaching style?</div>
            <div className="text-sm mt-2">Get full 3-month live access — pehle 20 seats par 67% off (₹9,999 only).</div>
            <Link to="/courses" className="btn-primary mt-3 w-full justify-center text-sm">
              <Sparkles className="w-4 h-4" /> Enroll Now
            </Link>
            <a href="https://wa.me/919131341638?text=Hi%2C%20demo%20class%20dekh%20li%2C%20enroll%20karna%20hai." target="_blank" rel="noreferrer" className="btn-ghost mt-2 w-full justify-center text-sm">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </div>
          <div className="mt-auto text-[10px] text-[var(--text-mute)] pt-4">
            Aap CodeWithKamlesh ka pre-recorded demo session dekh rahe hain. Live classes 3 mahine ke program me daily milengi.
          </div>
        </aside>
      </div>
    </div>
  );
}
